import { useSystemNotify } from "@/components/Chat/SystemNotify";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import useFirstModal from "@/components/FirstModal";
import Footer from "@/components/Footer";
import GdPermission from "@/components/GdPermission";
import GlobalBannerContainer from "@/components/GlobalBanner/Container";
import useGlobalUploader from "@/components/GlobalUploaderModal/useGlobalUploader";
import useImagePreview from "@/components/HBImagePreviewModal/useImagePreview";
import HBMessage from "@/components/HBMessage";
import HeaderNew from "@/components/Header/index-new";
import LaunchBottom from "@/components/LaunchBottom";
import Layout from "@/components/Layout";
import accountLocked from "@/components/LoginModal/shared/accountLocked";
import { usePinDetail } from "@/components/PinDetail/usePinDetail";
import SideButtonList from "@/components/SideButtonList";
import SideNav from "@/components/SideNav";
import { AD_POSITION } from "@/constants/ads";
import { COOKIE_KEY } from "@/constants/cookieKey";
import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import { PermissionCodes } from "@/constants/permissionCodes";
import useAppStore, { useInitStore } from "@/context/store";
import { getAdsTopConfig, getGrocery, getMe, getTopAds } from "@/services";
import initTracker from "@/tracker";
import { isGdMerged } from "@/utils/gaoding";
import "@/utils/gdSlsRum";
import { calculateHeaderHeight } from "@/utils/headerHeight";
import { GuideProvider } from "@/utils/hooks/useNewRegisterGuide";
import { usePageCache } from "@/utils/hooks/usePageCache";
import { useRafIntervalNotice } from "@/utils/hooks/useRafIntervalNotice";
import { isSupportWebp } from "@/utils/imageUrl";
import localStoragePolyfill from "@/utils/localStoragePolyfill";
import { memorizeFn } from "@/utils/memorizeFn";
import { safeParse } from "@/utils/safeParse";
import { initCacheor } from "@/utils/server/cacheor";
import { setTransferHeaders } from "@/utils/server/headerTransfer";
import { sidCheck, tokenCheck } from "@/utils/server/sidCheck";
import { stewardInstance } from "@/utils/steward";
import {
  getSideNavCollapsedState,
  useSideNavVisible,
} from "@/utils/useSideNavCollapse";
import { useEventListener, useMemoizedFn } from "ahooks";
import { ConfigProvider } from "antd";
import Browser from "bowser";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import Script from "next/script";
import { useEffect, useMemo } from "react";
import smoothscroll from "smoothscroll-polyfill";

const DownloadList = dynamic(
  import("@/components/MaterialDownloadModal/downloadList"),
  { ssr: false }
);

const BindPhoneGuideFooter = dynamic(
  import("@/components/Footer/BindPhoneGuide"),
  { ssr: false }
);

// 此处必须使用require https://www.npmjs.com/package/next-plugin-antd-less
require("/node_modules/antd/dist/antd.less");
require("../styles/globals.less");
if (typeof window !== "undefined") {
  localStoragePolyfill();
}

const hasFooterPages = [
  { path: "/vip/info", relative: true },
  { path: "/message", relative: false },
  { path: "/login", relative: false },
  { path: "/settings", relative: false },
  { path: "/cooperator/[cooperator]", relative: false },
  { path: "/vip/exchange", relative: false },
  { path: "/activities", relative: true },
  { path: "/activities/start", relative: false },
  { path: "/popular/[urlKey]", relative: true },
  { path: "/coupons/[activityCode]", relative: false },
  { path: "/pages/[[...slug]]", relative: false },
  { path: "/articles", relative: false },
  { path: "/articles/[code]", relative: false },
  { path: "/articles/[code]/[page]", relative: false },
  { path: "/gdmerge", relative: false },
  { path: "/promotions/vip", relative: true },
  { path: "/promotions/618", relative: true },
  { path: "/promotions/pintuan/[code]", relative: true },
  { path: "/landing/demo", relative: true },
];

const purePages = [
  { path: "/login" },
  { path: "/oauth/authorize" },
  { path: "/oauth/callback" },
  { path: "/signup" },
  { path: "/pay/transit" },
  { path: "/password/reset/[token]" },
  { path: "/browser" },
  { path: "/supernice" },
  { path: "/verify" },
  { path: "/share" },
  { path: "/bind/tel" },
  { path: "/gdmerge/callback" },

  {
    path: "/coupons/[activityCode]",
    type: "mobile", // pc、mobile或者不填
  },
  { path: "/vip/consult", type: "mobile" },
  { path: "/vip/buy" },
  { path: "/vip/exchange", type: "mobile" },
];

// 判断是否请求的是html结构
function isRequestingHtml(req) {
  const accept = req?.headers?.accept;
  const urlInfo = req?.url;
  // 需要通过两种方式判断用户请求的是否为html，请求头信息有可能会被修改
  return (
    (accept && accept.indexOf("text/html") !== -1) ||
    (urlInfo &&
      !/\.json$/.test(new URL(urlInfo, `https://${req.headers.host}`).pathname))
  );
}
// 浏览器兼容性检查
const getBrowser = (userAgent) => {
  const result = {
    compatible: true,
    os: "windows",
  };

  if (!userAgent || /spider/i.test(userAgent)) {
    result.compatible = true;
  } else {
    const ua = userAgent?.toLowerCase();
    const browser = Browser.getParser(ua);

    result.os = browser.getOSName(true);
  }

  return result;
};

function App(props) {
  const {
    Component,
    pageProps = {},
    os,
    isWeixin,
    isMobile,
    initStore,
    initErr,
  } = props;

  const router = useRouter();
  const pathname = router.pathname;
  const asPath = router.asPath;

  const env = process.env.NEXT_PUBLIC_DEPLOY_ENV || "prod";
  useInitStore(initStore);
  usePageCache();
  useFirstModal();
  const {
    wrapperWidth: width,
    config: appConfig,
    user,
    ads,
    setLastBoards,
    dialogs,
    globalNotice,
    columnSize,
  } = useAppStore(
    ({
      user,
      wrapperWidth,
      config,
      setLastBoards,
      ads,
      dialogs,
      globalNotice,
      columnSize,
    }) => ({
      user,
      wrapperWidth,
      config,
      setLastBoards,
      ads,
      dialogs,
      globalNotice,
      columnSize,
    })
  );

  initTracker({ isMobile, user });
  useRafIntervalNotice(globalNotice);

  const bannerAd = Array.isArray(ads)
    ? ads.find((ad) => ad?.position?.id === AD_POSITION.TOP)
    : null;
  const bannerNotice = globalNotice?.visible && globalNotice?.content;
  const totalHeaderHeight = calculateHeaderHeight({
    hasBannerAd: !!bannerAd,
    hasBannerNotice: !!bannerNotice,
  });

  const isHome = pathname === "/";

  const sideNavVisible = useSideNavVisible(); // 根据页面判断是否需要左侧导航

  // 当totalHeaderHeight变化时更新store中的headerHeight
  useEffect(() => {
    useAppStore.setState({
      headerHeight: totalHeaderHeight,
    });
  }, [totalHeaderHeight]);

  const { hasFooter, isFooterRelative } = useMemo(() => {
    const item = hasFooterPages.find((item) => item.path === pathname);
    return {
      hasFooter: !!item,
      isFooterRelative: item?.relative,
    };
  }, [pathname]);

  const { isPurePage } = useMemo(() => {
    const item = purePages.find((item) => item.path === pathname);
    let isPurePage = !!item;
    if (item?.type === "mobile") {
      isPurePage = isMobile;
    } else if (item?.type === "pc") {
      isPurePage = !isMobile;
    }

    return {
      isPurePage,
    };
  }, [isMobile, pathname]);

  useEffect(() => {
    if (initErr?.msg) {
      // 对此类错误进行特殊处理，账号异常需要重置密码
      if (initErr.msg.includes("重新设置账号密码")) {
        accountLocked(initErr);
        return;
      }

      // 其他设备登录
      if (initErr.status === 401 && initErr.detail) {
        import("@/components/LoginModal/otherDeviceLogin").then(
          ({ OtherDeviceLoginInfo }) => {
            OtherDeviceLoginInfo.open(initErr.detail);
          }
        );

        return;
      }

      HBMessage.error(initErr.msg);
    }
  }, [initErr]);

  useEffect(() => {
    // 如果浏览器不支持scroll behavior（比如Safari就不支持），就执行
    const parser = Browser.getParser(window.navigator.userAgent);
    // smoothscroll.polyfill() 自带检测是否支持smoothscroll，来决定是否执行polyfill，
    // 但是其自带的监测方式无法在 safari一些版本中检测出来
    // 所以这里加上根据浏览器版本进行判断是否执行 polyfill
    // 参考链接：https://caniuse.com/scrollintoview
    // https://github.com/iamdustan/smoothscroll/blob/master/src/smoothscroll.js#L10
    const smoothSupport = parser.satisfies({
      safari: "<=15.6",
    });
    if (smoothSupport) {
      // 强制执行polyfill
      window.__forceSmoothScrollPolyfill__ = true;
    }
    smoothscroll.polyfill();
  }, []);

  useEffect(() => {
    document.body.classList.add(`os-${os}`);
    isMobile && document.body.classList.add(`isMobile`);
    isWeixin && document.body.classList.add(`isWeixin`);
    window.GLOBAL_INFO_OS = os; // 写入全局对象上
  }, [isMobile, isWeixin, os]);

  useEventListener("storage", (event) => {
    if (event.key === LOCAL_STORAGE_KEY.UPDATE_LAST_BOARDS) {
      const lastBoards = event.newValue;
      if (lastBoards) {
        try {
          setLastBoards(JSON.parse(lastBoards));
          window.localStorage.removeItem(LOCAL_STORAGE_KEY.UPDATE_LAST_BOARDS);
        } catch (e) {
          console.error("更新最近采集失败");
        }
      }
    }
  });

  useGlobalUploader();
  useSystemNotify();
  useImagePreview();

  useEffect(() => {
    if (user) {
      stewardInstance.init();
    }
  }, [user]);

  useEffect(() => {
    // 灰色主题的filter会与fixed冲突，导致页面样式混乱
    // https://juejin.cn/post/6844904117974859783
    if (appConfig?.grayTheme && pathname === "/") {
      document.documentElement.classList.add("theme-gray");
    } else {
      document.documentElement.classList.remove("theme-gray");
    }
  }, [appConfig?.grayTheme, pathname]);
  // const { wrapperStyle, mainStyle } = useMemo(() => {
  //   return getStyle(pathname, width);
  // }, [pathname, width]);

  // 通过css来控制稿定模块的入口显示隐藏
  useEffect(() => {
    if (isGdMerged(user)) {
      document.body.classList.remove("not-gdmerged");
    } else {
      document.body.classList.add("not-gdmerged");
    }
  }, [user]);

  usePinDetail();

  // 页面内容
  const getPageContent = useMemoizedFn(() => {
    const { staticPageConfig } = pageProps;

    if (isPurePage || staticPageConfig?.purePage) {
      return <Component {...pageProps} />;
    }

    /* 导航栏 */
    let headerComponent = (
      <HeaderNew
        isMobile={isMobile}
        searchWord={pageProps?.searchWord}
        ads={ads}
        sideNavVisible={sideNavVisible}
      />
    );

    /* 页脚 */
    let footerComponent = hasFooter && <Footer relative={isFooterRelative} />;

    // 静态页面
    if (staticPageConfig) {
      if (!staticPageConfig.nav) {
        headerComponent = null;
      }

      if (!staticPageConfig.footer) {
        footerComponent = null;
      }
    }

    return (
      <Layout
        header={headerComponent}
        sideNav={<SideNav />}
        topBar={<GlobalBannerContainer />}
        sideNavVisible={sideNavVisible}
        extendContentToHeader={isHome}
      >
        {/* 页面内容 */}
        <Component {...pageProps} />

        {/* 右下角悬浮按钮组 */}
        {!isHome && !["/albums/[urlname]", "/create"].includes(pathname) && (
          <SideButtonList />
        )}

        {/* 左下角下载列表 */}
        <GdPermission code={PermissionCodes.materialLogs}>
          <DownloadList />
        </GdPermission>

        {/* 全局底部登录栏 */}
        <LaunchBottom
          inlinks={pageProps?.inlinks}
          disabledModalOpenWidth={isHome}
        />

        {/* 页脚 */}
        {footerComponent}

        <BindPhoneGuideFooter />
      </Layout>
    );
  });

  function removeCacheParam(url) {
    return url.replace(/([?&])_cache=[^&]*(&|$)/, (match, p1, p2) => {
      // 如果是第一个参数，用 '?' 保留剩余部分；否则用 '&'。
      return p1 === "?" ? (p2 === "&" ? "?" : "") : p2;
    });
  }

  return (
    <>
      <Head>
        <title>
          花瓣网 -
          陪你做生活的设计师（创意图片大全、设计灵感图库、高清图片素材）
        </title>

        <meta charset="utf-8" />
        <meta
          name="keywords"
          content="花瓣网,花瓣,图片大全,创意图片,设计灵感"
        />
        <meta
          name="description"
          content="花瓣网,设计师寻找灵感的平台!这里汇聚海量高清创意图片与设计素材,通过智能采集工具助你高效整理灵感.无论是保存优质素材,规划设计方案,还是分享个人创意收藏,花瓣网都能满足设计师需求,让灵感采集与创作落地无缝衔接。"
        />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
        <meta name="renderer" content="webkit" />
        <meta name="copyright" content="huaban.com" />
        <meta name="referrer" content="always" />

        <link rel="apple-touch-icon" href="/img/touch-icon-iphone.png" />
        <link
          rel="apple-touch-icon"
          sizes="76x76"
          href="/img/touch-icon-ipad.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="120x120"
          href="/img/touch-icon-iphone-retina.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="152x152"
          href="/img/touch-icon-ipad-retina.png"
        />
        <link
          rel="canonical"
          // removeCacheParam 避免保存缓存的参数
          href={`https://huaban.com${removeCacheParam(asPath)}`}
        />
        {!isMobile && (
          <meta
            httpEquiv="mobile-agent"
            content={`format=html5;url=https://m.huaban.com${asPath}`}
          />
        )}
      </Head>

      {/* 微软统计 */}
      <Script id="ms_clarity" strategy="beforeInteractive">
        {`
          (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "rqhfezlm6u");
        `}
      </Script>

      {/* 百度统计脚本 */}
      <Script
        id="baidu-tongji"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            var _hmt = _hmt || [];
            (function() {
              var hm = document.createElement("script");
              hm.src = "https://hm.baidu.com/hm.js?d4a0e7c3cd16eb58a65472f40e7ee543";
              var s = document.getElementsByTagName("script")[0]; 
              s.parentNode.insertBefore(hm, s);
              hm.onload = function(){
                const htmlEl = document.body.parentElement;
                _hmt.push(["_setUserProperty", {"has_plugin": htmlEl.className.indexOf("hbChromeExtInstalled")>=0?1:0}]);
                _hmt.push(["_setUserProperty", {"plugin_version": htmlEl.getAttribute("data-hb-chrome-ext-installed")}]);
              }
            })();
          `,
        }}
      />
      <Script
        strategy="beforeInteractive"
        src="https://cdn.dancf.com/packages/gaoding/gd-tracker/2.19.0/gd-tracker.browser.js"
      ></Script>
      <Script
        src="https://cdn.dancf.com/packages/gaoding/web-canary-client/v1.8.3/index.umd.js"
        strategy="beforeInteractive"
      ></Script>
      {/* 覆盖web-canary-client白名单，避免所有请求带上相关请求头标识 */}
      <Script
        id="CanaryClientInit"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            if(window.GdWebCanaryClient && window.GdWebCanaryClient.default){
              window.GdWebCanaryClient.default.setWhiteList(["gd.huaban.com", "api${
                env === "prod" ? "" : "-" + env
              }.huaban.com", "${
            env === "prod" ? "" : env + "."
          }huaban.com"], true);
        }
          `,
        }}
      ></Script>
      <Script
        src="https://cdn.dancf.com/packages/gaoding/gd-sls-rum/v1.23.1/index.js"
        strategy="beforeInteractive"
      ></Script>

      {/* 没有特殊用处，纯粹为了让插件的截图功能可以使用 */}
      <div id="header"></div>

      <ConfigProvider autoInsertSpaceInButton={false}>
        <GuideProvider>
          {getPageContent()}
          {dialogs.map((dialog) => dialog)}
        </GuideProvider>
      </ConfigProvider>
    </>
  );
}

export default function WrapApp(props) {
  return (
    <ErrorBoundary>
      <App {...props} />
    </ErrorBoundary>
  );
}
const getGlobalConfig = memorizeFn(() =>
  getGrocery("global-config", { serverSide: true, timeout: 2500 }).catch(
    () => ({})
  )
);

// 获取全局公告
const getGlobalNotice = memorizeFn(() =>
  getGrocery("global-notice", { serverSide: true, timeout: 2500 }).catch(
    () => ({})
  )
);

// getInitialProps会在客户端运行(路由变化时)，需要确保客户端的兼容性
// https://nextjs.org/docs/api-reference/data-fetching/get-initial-props
WrapApp.getInitialProps = async ({ ctx }) => {
  const { req, res } = ctx;
  const userAgent =
    (typeof window !== "undefined"
      ? window?.navigator?.userAgent
      : req?.headers["user-agent"]) || "";
  const browserInfo = getBrowser(userAgent);
  const cookie = req?.headers?.["cookie"];
  const host = req?.headers?.["host"];
  const initStore = {};
  let initErr = null;
  const isPageCache = res?.isPageCache;

  // 服务端渲染时，设置transferHeaders,透传部分header
  if (typeof window === "undefined") {
    setTransferHeaders(req?.headers);
    const envInfo = require("@/utils/server/envInfo");
    Object.assign(initStore, { envInfo: envInfo.default });
  }

  isSupportWebp(userAgent);
  const isWeixin = userAgent.toLowerCase().includes("micromessenger");
  const isMobile =
    getIsMobile(userAgent) ||
    // 匹配移动端域名
    /^(m(-stage|-fat|-dev)?\.)[^.]+\.(com)$/.test(host);

  // 兼容客户端执行情况. 客户端不执行,服务端只有请求html的时候再执行
  if (typeof window === "undefined" && isRequestingHtml(req)) {
    // 获取redis缓存实例
    initCacheor(res);
    const adTopConfig = await getAdsTopConfig();

    const [config, user, ads, globalNotice] = await Promise.all([
      getGlobalConfig(),
      !isPageCache && (sidCheck(cookie) || tokenCheck(cookie))
        ? getMe(cookie)
        : null,
      !isPageCache
        ? getTopAds({
            cookie,
            userAgent,
            url: req?.url,
            adTopConfig,
          })
        : [],
      !isPageCache ? getGlobalNotice() : {},
    ]);

    const bannerAd = Array.isArray(ads)
      ? ads.find((ad) => ad?.position?.id === AD_POSITION.TOP)
      : null;
    // 判断是否有全局公告
    const bannerNotice = globalNotice?.visible && globalNotice?.content;
    // 有公告或者广告设置为120，没有则设置为60
    // const headerHeight = bannerAd || bannerNotice ? 120 : 60;
    const hasGlobalBanner = !!(bannerAd || bannerNotice);
    // const adsData = Array.isArray(ads) ? ads?.pop() : null;
    const initImageConfig = req?.cookies[COOKIE_KEY.IMAGE_URL_CONFIG];

    // 计算宽度
    const pageSetting = safeParse(
      req?.cookies[LOCAL_STORAGE_KEY.HUABAN_PAGE_SETTING],
      () => req?.cookies[LOCAL_STORAGE_KEY.HUABAN_PAGE_SETTING]
    );
    const itemWidth = pageSetting?.columnSize || 240;
    const itemGap = 16;
    let num = 20;
    if (pageSetting?.cols) {
      num = pageSetting?.cols;
    }

    // 获取侧边栏收起状态
    const isSideNavCollapsed = getSideNavCollapsedState(req, isPageCache);

    useAppStore.setState({ config });

    Object.assign(initStore, {
      adTopConfig,
      config,
      // 兼容超时情况，超时返回一个request对象
      user: user?.user_id ? user : null,
      userReady: !isPageCache,
      isMobile,
      ads: ads,
      column: num,
      columnSize: itemWidth,
      initImageConfig,
      wrapperWidth: num * (itemWidth + itemGap) - itemGap,
      // headerHeight,
      globalBannerVisible: hasGlobalBanner,
      globalNotice,
      isPageCache,
      // 初始化不显示筛选面板
      isFilterVisible: false,
      isCollapsed: isSideNavCollapsed,
    });

    // 未登录时返回的是404，不需要处理错误信息，其他设备登录时，有status参数，需要处理错误信息
    if (
      (user?.err && user?.err !== 404) ||
      (user?.err === 404 && user?.status === 401)
    ) {
      initErr = user;
    }

    // 拒绝未登录用户访问
    if (config?.refuseNoLogin && !initStore.user) {
      res.writeHead(307, {
        Location: "/unavailable.html",
      });
      return res.end();
    }

    // 拒绝所有访问
    if (config?.refuse) {
      res.writeHead(307, {
        Location: "/unavailable.html",
      });
      return res.end();
    }

    req.user = initStore.user;
  }
  return {
    os: browserInfo.os,
    isWeixin,
    isMobile,
    initStore,
    ...(initErr ? { initErr } : {}),
  };
};

function getIsMobile(ua, options) {
  options = options || {};
  if (!ua) return false;

  ua = ua.toLowerCase();
  var isIos = !!ua.match(/ip(?:ad|od|hone)/i);
  var isIpad = !!ua.match(/ipad/i);
  var isAndroid = !!ua.match(/(?:android|okhttp)/i);
  var isUcmobile = ua.match(/(ucbrowser|mobile|ucweb)/gi);
  var isWindowsPhone = ua.match(/(Windows Phone)/gi);
  var isMiPad = ua.match(/(MI PAD)/gi);

  return (
    (isIos || isAndroid || isUcmobile || isWindowsPhone) &&
    (!options.excludePad || (!isIpad && !isMiPad))
  );
}
